"use client";

import {
  createContext,
  Dispatch,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";

interface LandingSections {
  heroRef: RefObject<HTMLDivElement>;
  pricingRef: RefObject<HTMLDivElement>;
  parentsSectionRef: RefObject<HTMLDivElement>;
  aboutUsSectionRef: RefObject<HTMLDivElement>;
  partnersRef: RefObject<HTMLDivElement>;
  contactRef: RefObject<HTMLDivElement>;
  tryWithRealQuestionsRef: RefObject<HTMLDivElement>;
  whyWisWizRef: RefObject<HTMLDivElement>;
}

type LandingContext = {
  sectionRefs: LandingSections;

  activeSection: number;
  setActiveSection: Dispatch<SetStateAction<number>>;

  scrollToSection: (ref: RefObject<HTMLDivElement>, sectionId: number) => void;

  showFeedbackModal: boolean;
  setFormSubmitted: Dispatch<SetStateAction<boolean>>;
  formSubmitted: boolean;
  setShowFeedbackModal: Dispatch<SetStateAction<boolean>>;
  feedbackType: string;
  setFeedbackType: Dispatch<SetStateAction<string>>;
  feedbackTitle: string;
  setFeedbackTitle: Dispatch<SetStateAction<string>>;
  feedbackDetails: string;
  setFeedbackDetails: Dispatch<SetStateAction<string>>;
  feedbackEmail: string;
  setFeedbackEmail: Dispatch<SetStateAction<string>>;
};

const LandingContext = createContext<LandingContext | null>(null);

export const useLandingContext = () => {
  const context = useContext(LandingContext);
  if (context === null) {
    throw Error("No LandingContext available");
  }
  return context;
};

export const LandingContextProvider = ({ children }: PropsWithChildren) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const parentsSectionRef = useRef<HTMLDivElement>(null);
  const aboutUsSectionRef = useRef<HTMLDivElement>(null);
  const partnersRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const tryWithRealQuestionsRef = useRef<HTMLDivElement>(null);
  const whyWisWizRef = useRef<HTMLDivElement>(null);
  const [feedbackType, setFeedbackType] = useState("");
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [activeSection, setActiveSection] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const scrollToSection = useCallback(
    (ref: React.RefObject<HTMLDivElement>, sectionId: number) => {
      if (ref.current) {
        const elementPosition =
          ref.current.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 80; // subtract header height
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        setActiveSection(sectionId);
      }
    },
    [setActiveSection]
  );

  const sectionRefs: LandingSections = useMemo(() => {
    return {
      heroRef,
      pricingRef,
      parentsSectionRef,
      aboutUsSectionRef,
      partnersRef,
      contactRef,
      tryWithRealQuestionsRef,
      whyWisWizRef
    };
  }, []);

  const providerValue = useMemo(() => {
    return {
      sectionRefs,
      activeSection,
      setActiveSection,
      scrollToSection,
      showFeedbackModal,
      setShowFeedbackModal,

      formSubmitted,
      feedbackType,
      feedbackTitle,
      feedbackEmail,
      feedbackDetails,

      setFormSubmitted,
      setFeedbackType,
      setFeedbackTitle,
      setFeedbackEmail,
      setFeedbackDetails
    };
  }, [
    activeSection,
    sectionRefs,
    scrollToSection,
    showFeedbackModal,
    setShowFeedbackModal,

    formSubmitted,
    feedbackType,
    feedbackTitle,
    feedbackEmail,
    feedbackDetails
  ]);

  return (
    <LandingContext.Provider value={providerValue}>
      {children}
    </LandingContext.Provider>
  );
};
